import React from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, BookOpen, Download, Star, BadgeCheck, GraduationCap, ChevronDown, ChevronUp } from "lucide-react";

function formatMetric(value) {
  if (value === null || value === undefined) return "0";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${num}`;
}

function getInitial(name = "") {
  return name.trim()?.charAt(0)?.toUpperCase() || "U";
}

const CITY_LABELS = {
  khartoum: { en: "Khartoum", ar: "الخرطوم" },
  omdurman: { en: "Omdurman", ar: "أم درمان" },
  khartoum_north: { en: "Khartoum North", ar: "بحري" },
  port_sudan: { en: "Port Sudan", ar: "بورتسودان" },
  wad_madani: { en: "Wad Madani", ar: "ود مدني" },
  kassala: { en: "Kassala", ar: "كسلا" },
};

function formatCity(key, isRTL) {
  if (!key) return "";
  const entry = CITY_LABELS[key];
  if (entry) return isRTL ? entry.ar : entry.en;
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export default function SellerInfoCard({
  seller,
  onMessageClick,
  onViewOtherListings,
  listingsExpanded = false,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  if (!seller) return null;

  const {
    name = "Unknown Seller",
    university_name = "",
    university_name_ar = "",
    faculty = "",
    faculty_ar = "",
    is_verified = false,
    student_verified = false,
    books_shared = 0,
    books_shared_count = 0,
    downloads = 0,
    total_downloads = 0,
    average_rating = 0,
    reviews_count = 0,
    city = "",
  } = seller;

  const verified = is_verified || student_verified;
  const sharedCount = books_shared || books_shared_count;
  const downloadCount = downloads || total_downloads;
  const universityDisplay = isRTL && university_name_ar ? university_name_ar : university_name;
  const facultyDisplay = isRTL && faculty_ar ? faculty_ar : faculty;
  const cityDisplay = formatCity(city, isRTL);

  return (
    <section className="seller-card" data-testid="seller-card" aria-label="Seller information" dir={isRTL ? "rtl" : "ltr"}>
      {/* Row 1: avatar + identity */}
      <div className="seller-card__top">
        <div className="seller-card__identityWrap">
          <div className="seller-card__avatar" aria-hidden="true">
            {getInitial(name)}
          </div>

          <div className="seller-card__identity">
            <div className="seller-card__nameRow">
              <h3 className="seller-card__name" title={name}>
                {name}
              </h3>

              {verified && (
                <span className="seller-card__verifiedInline" title="Verified Student" data-testid="verified-student-tag">
                  <BadgeCheck size={16} />
                </span>
              )}
            </div>

            {(universityDisplay || faculty || cityDisplay) && (
              <div className="seller-card__meta">
                {universityDisplay ? (
                  <div className="seller-card__metaItem seller-card__metaItem--university" data-testid="seller-university">
                    <GraduationCap size={14} />
                    <span title={universityDisplay}>{universityDisplay}</span>
                  </div>
                ) : null}

                {facultyDisplay ? (
                  <div className="seller-card__metaItem">
                    <span title={facultyDisplay}>{facultyDisplay}</span>
                  </div>
                ) : null}

                {cityDisplay ? (
                  <div className="seller-card__metaItem">
                    <span title={cityDisplay}>{cityDisplay}</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: verified / university pills */}
      <div className="seller-card__badges">
        {verified && (
          <span className="seller-card__badge seller-card__badge--verified">
            <BadgeCheck size={14} />
            {t("bookdeta.verifiedStudent")}
          </span>
        )}

        {universityDisplay && (
          <span className="seller-card__badge seller-card__badge--neutral" title={universityDisplay}>
            {universityDisplay}
          </span>
        )}
      </div>

      {/* Row 3: stats */}
      <div className="seller-card__stats" data-testid="seller-metrics">
        <div className="seller-card__stat">
          <BookOpen size={16} />
          <div className="seller-card__statText">
            <strong>{formatMetric(sharedCount)}</strong>
            <span>{t("bookdeta.booksShared")}</span>
          </div>
        </div>

        <div className="seller-card__stat">
          <Download size={16} />
          <div className="seller-card__statText">
            <strong>{formatMetric(downloadCount)}</strong>
            <span>{t("bookdeta.downloads")}</span>
          </div>
        </div>

        <div className="seller-card__stat">
          <Star size={16} />
          <div className="seller-card__statText">
            <strong>{Number(average_rating || 0).toFixed(1)}</strong>
            <span>{t("bookdeta.rating")}</span>
          </div>
        </div>

        {reviews_count > 0 && (
          <div className="seller-card__stat">
            <Star size={16} />
            <div className="seller-card__statText">
              <strong>{formatMetric(reviews_count)}</strong>
              <span>{t("bookdeta.reviews")}</span>
            </div>
          </div>
        )}
      </div>

      {/* Contact Seller CTA */}
      <button
        type="button"
        className="seller-card__cta"
        onClick={onMessageClick}
        data-testid="contact-seller-cta"
      >
        <MessageCircle size={20} />
        <span>{t("bookdeta.contactSellerCTA")}</span>
      </button>

      {/* Footer link */}
      <button
        type="button"
        className="seller-card__linkBtn"
        onClick={onViewOtherListings}
        data-testid="seller-listings-link"
      >
        <span>{isRTL ? `المزيد من ${name}` : `More from ${name}`}</span>
        {listingsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
    </section>
  );
}
